"use client";

import { useEffect, useState } from "react";
import type { ActiveSpecial } from "@/lib/payload/public";
import { safeHref } from "@/components/public/safe-href";

/*
 * `announcement-banner / countdown-urgency-bar` (Editorial + Bold-Dark/Luxe).
 *
 * A time-boxed event strip: a centered one-line band on `--color-surface-2`,
 * framed top+bottom with `--color-border-strong` hairlines so it reads as a
 * distinct event band without a saturated fill. The message sits beside a live
 * unit-chip countdown to the special's `endsAt`: each chip is `--color-surface-3`
 * at `--radius-sm` with `font-mono tabular-nums` digits (stable width as the
 * numerals tick) and a `--color-foreground-muted` unit label. The leading
 * deadline marker carries `--color-brand-accent` to signal urgency.
 *
 * SSR-safe ticking: the component is only mounted by the dispatcher when
 * `endsAt` is in the future, so it always has a deadline. The lazy `useState`
 * initializer computes a snapshot from `endsAt - Date.now()`, then an interval
 * re-ticks every second after hydration. The server's SSR clock read and the
 * client's first hydration read are moments apart, so each digit's text node
 * carries `suppressHydrationWarning` (it must sit on the text-bearing element,
 * not an ancestor — the flag only covers an element's own direct content); React
 * keeps the server digit and the first interval tick corrects it within a second.
 *
 * Like the split bar, an optional dismiss "X" hides it for the session (the
 * prior decision read lazily from `sessionStorage`, guarded for SSR).
 */
export function SpecialsBannerCountdown({ special }: { special: ActiveSpecial }) {
  const deadline = special.endsAt ? new Date(special.endsAt).getTime() : 0;
  const storageKey = `specials-dismiss:${hashText(special.text)}`;
  const [remaining, setRemaining] = useState(() => computeRemaining(deadline));
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));

  // External sync: re-derive the remaining time every second from the wall
  // clock. The interval callback's setState is the synchronization point; the
  // initial snapshot comes from the lazy `useState` initializer above.
  useEffect(() => {
    const id = window.setInterval(() => setRemaining(computeRemaining(deadline)), 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  // Once the live clock crosses the deadline post-hydration, retire the strip.
  if (dismissed || remaining.expired) {
    return null;
  }

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(storageKey, "1");
    } catch {
      // best-effort
    }
  };

  const hasCta = Boolean(special.ctaHref && special.ctaLabel);

  return (
    <div className="relative border-y border-(--color-border-strong) bg-(--color-surface-2)">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center justify-center gap-2 px-6 py-2 text-center md:flex-row md:gap-4 md:px-8">
        <p className="text-sm text-(--color-foreground)">
          <span className="font-medium text-(--color-brand-accent)">Ends in</span> {special.text}
        </p>
        <div
          suppressHydrationWarning
          className="flex shrink-0 items-center gap-1.5 font-mono tabular-nums"
        >
          <CountdownChip value={remaining.days} label="days" />
          <Separator />
          <CountdownChip value={remaining.hours} label="hrs" />
          <Separator />
          <CountdownChip value={remaining.minutes} label="min" />
          <Separator />
          <CountdownChip value={remaining.seconds} label="sec" />
        </div>
        {hasCta ? (
          <a
            href={safeHref(special.ctaHref as string)}
            className="shrink-0 text-[13px] font-semibold text-(--color-brand-accent) underline-offset-4 hover:underline"
          >
            {special.ctaLabel} →
          </a>
        ) : null}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute top-1/2 right-3 -translate-y-1/2 rounded-(--radius-full) p-1 text-base leading-none text-(--color-foreground-subtle) transition-colors hover:text-(--color-foreground) md:right-4"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

function CountdownChip({ value, label }: { value: number; label: string }) {
  return (
    <span className="inline-flex items-baseline gap-1 rounded-(--radius-sm) bg-(--color-surface-3) px-2 py-1">
      {/* The digit is a live clock read: the server's SSR snapshot and the client's
          first hydration render are moments apart, so the text legitimately differs.
          `suppressHydrationWarning` MUST sit on the text-bearing element itself —
          it only covers an element's own direct content, not nested descendants, so
          a container-level flag does not reach here. React keeps the server digit
          (no warning, no subtree regeneration) and the 1s interval corrects it. */}
      <span suppressHydrationWarning className="text-sm font-semibold text-(--color-foreground)">
        {pad(value)}
      </span>
      <span className="text-[10px] tracking-[0.08em] text-(--color-foreground-muted) uppercase">
        {label}
      </span>
    </span>
  );
}

function Separator() {
  return (
    <span aria-hidden="true" className="text-(--color-foreground-faint)">
      :
    </span>
  );
}

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

function computeRemaining(deadlineMs: number): Remaining {
  const diff = deadlineMs - Date.now();
  if (!Number.isFinite(diff) || diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: false,
  };
}

function pad(n: number): string {
  return String(Math.max(0, n)).padStart(2, "0");
}

/** Reads the session-dismiss flag; returns false on the server (no `window`) or when blocked. */
function readDismissed(storageKey: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.sessionStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

/** Stable, dependency-free hash so the session key tracks the specific promo text. */
function hashText(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) {
    h = (h << 5) - h + text.charCodeAt(i);
    h |= 0;
  }
  return (h >>> 0).toString(36);
}
